import type * as React from 'react'

import { Button, CoachMark, Flex } from '@buffer-mono/popcorn'
import type { ChannelType } from '@buffer-mono/popcorn'

import { OneWayLikeIllustration } from './OneWayLikeIllustration'

import type { JSX } from 'react'

type FirstTimeLikeCoachmarkProps = {
  /** Whether the coachmark is shown (owned by `useFirstTimeLikeCoachmark`). */
  open: boolean
  /** Called when the actor dismisses via "Got it" (or the overlay). */
  onDismiss: () => void
  /** Source network, for the illustration. */
  service: ChannelType
  /** Display name of the source network, used in the copy. */
  networkName: string
  /** The reaction control the coachmark points at. */
  children: React.ReactNode
}

/**
 * First-time coachmark that explains one-way like behavior on non-interoperable
 * networks: liking in Buffer pushes to the network, but likes added there won't
 * appear in Buffer. Shown once per network (see `useFirstTimeLikeCoachmark`),
 * anchored to the reaction the actor just used.
 */
export const FirstTimeLikeCoachmark = ({
  open,
  onDismiss,
  service,
  networkName,
  children,
}: FirstTimeLikeCoachmarkProps): JSX.Element => {
  // Always render the trigger in the same place (inside the overlay) so the
  // reaction the actor just clicked isn't unmounted/refocused when the
  // coachmark opens; gate only the spotlight and content on `open`.
  return (
    <CoachMark open={open} onDismiss={onDismiss} dismissTimeout={null}>
      <CoachMark.Overlay>
        {open && <CoachMark.Spotlight />}
        {children}
      </CoachMark.Overlay>
      {open && (
        <CoachMark.Content side="top" align="center" showCloseIcon={false}>
          <Flex direction="column" gap="md">
            <OneWayLikeIllustration service={service} />
            <Flex direction="column" gap="xs">
              <CoachMark.Title>{`Likes for ${networkName}`}</CoachMark.Title>
              <CoachMark.Description>
                {`Your like will appear on ${networkName} ✅ Keep in mind that it `}
                <strong>works one way</strong>
                {` for this channel - if you add a like on ${networkName}, it won't show in Buffer.`}
              </CoachMark.Description>
            </Flex>
          </Flex>
          <CoachMark.Footer>
            <CoachMark.Dismiss>
              <Button variant="primary" onClick={onDismiss}>
                Got It
              </Button>
            </CoachMark.Dismiss>
          </CoachMark.Footer>
        </CoachMark.Content>
      )}
    </CoachMark>
  )
}
